import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ClipboardDocumentListIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import MainButton from '@/components/ui/custom/buttons/MainButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import ModalBank from '@/components/ui/custom/modals/ModalBank/ModalBank';
import { activitiesQueryKeys } from '@/data/services/querykeys';
import { ResourceFields } from '@/data/services/_deprecated/resourceServices';
import Resource from '@/models/_deprecated/Resource';
import Activity from '@/models/_deprecated/Activity';
import alert from '@/utils/shared/alert';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import { getErrorMessage } from '@/utils/getErrorMessage';
import QuestionIcon from '@/components/admin/activities/QuestionIcon';
import ModalDisable from '@/components/ui/custom/modals/ModalDisable';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import useListService from '@/data/hook/useListService';
import { REQUEST_STALE_TIME_IN_MS as staleTime } from '@/constants';
import { LoadingIcon } from '@/components/icons';
import ComponentGuard from '@/components/ui/custom/ComponentGuard';
import { UserTypeEnum } from '@/models/User';

type ResourceActivitiesProps = {
  resource: Resource;
  editing?: boolean;
  updateResource(
    resourceId: number,
    body: Partial<ResourceFields>,
  ): Promise<void>;
  loading?: boolean;
  editingAllowed?: boolean;
};

export default function ResourceActivities({
  resource,
  editing,
  updateResource,
  loading,
  editingAllowed,
}: ResourceActivitiesProps) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { t: tResourceActivities } = useTranslation('translation', {
    keyPrefix: 'resourceActivities',
  });

  const { t: editRulesT } = useTranslation('translation', {
    keyPrefix: 'adminPage.editCourseRules',
  });

  const [activityId, setActivityId] = useState<number>();

  const { activitiesIds = [] } = resource;

  const {
    results: activities,
    isInitialLoading: loadingActivities,
    isError: activityError,
  } = useListService({
    ...activitiesQueryKeys.list({
      id: activitiesIds,
    })._ctx.infinity,
    enabled: !!activitiesIds.length,
    staleTime,
  });

  const [openModal, setOpenModal] = useState(false);

  const onUseActivity = async (activity: Activity) => {
    const currentIds = activitiesIds;

    const isAlreadyInArray = currentIds.includes(activity.id);

    if (isAlreadyInArray) {
      alert.warning(tResourceActivities('activityAlreadyIncluded'));
    } else {
      try {
        setActivityId(activity.id);
        await updateResource(resource.id, {
          activitiesIds: [...currentIds, activity.id],
        });
      } catch (error) {
        alert.error(getErrorMessage(error));
      } finally {
        setOpenModal(false);
        setActivityId(undefined);
      }
    }
  };

  const onRemoveActivity = async (activityId: number) => {
    const currentIds = activitiesIds;

    await updateResource(resource.id, {
      activitiesIds: currentIds.filter(id => id !== activityId),
    });
  };

  return (
    <ComponentGuard roles={[UserTypeEnum.SUPER_ADMIN]}>
      <section className="flex gap-2.5">
        <ul className="flex flex-col gap-2.5">
          <ConditionalRenderer condition={loadingActivities}>
            <LoadingIcon className="w-10 h-10 text-primary/40 self-center" />
          </ConditionalRenderer>

          <ConditionalRenderer condition={activityError}>
            <Text text={getErrorMessage(activityError)} />
          </ConditionalRenderer>

          <ConditionalRenderer condition={activities.length}>
            {activities.map(activity => (
              <ResourceActivity
                key={activity?.id}
                activity={activity}
                onRemoveActivity={onRemoveActivity}
                editing={editing}
                loadingRemove={loading}
                editingAllowed={editingAllowed}
              />
            ))}
          </ConditionalRenderer>

          <ConditionalRenderer condition={editing}>
            <ConditionalWrapper
              condition={!editingAllowed}
              wrapper={<Tooltip text={editRulesT('cantModify')} />}
            >
              <MainButton
                dataTestId="resourceActivityButton"
                className="font-500 px-1.5 py-1"
                text={t('activity')}
                onClick={() => setOpenModal(prev => !prev)}
                icon={<PlusIcon className="stroke-[3]" />}
                disabled={!editingAllowed}
              />
            </ConditionalWrapper>
          </ConditionalRenderer>
        </ul>

        <ModalBank
          visible={openModal}
          onClose={() => setOpenModal(false)}
          initial="activities"
          onUseActivity={onUseActivity}
          selectedId={activityId}
          includedIds={activitiesIds}
        />
      </section>
    </ComponentGuard>
  );
}

type ResourceActivityProps = {
  activity?: Activity;
  onRemoveActivity(activityId: number): Promise<void>;
  editing?: boolean;
  loadingRemove?: boolean;
  editingAllowed?: boolean;
};

function ResourceActivity({
  activity,
  editing,
  onRemoveActivity,
  loadingRemove,
  editingAllowed,
}: ResourceActivityProps) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { t: editRulesT } = useTranslation('translation', {
    keyPrefix: 'adminPage.editCourseRules',
  });

  const [removing, setRemoving] = useState(false);

  if (activity) {
    return (
      <li className="flex gap-2.5 items-center">
        <ModalDisable
          isRequesting={loadingRemove}
          visible={removing}
          translationString="deleteResourceActivity"
          onClickConfirm={() => onRemoveActivity(activity.id)}
          onClickCancel={() => setRemoving(false)}
        />
        <ClipboardDocumentListIcon className="w-5 text-primary" />
        <Text text={activity.name} className="h-fit max-w-96" />
        {activity.questions.map(({ type }, index) => (
          <span
            className="text-16 flex items-center gap-4 activity-list-item text-primary"
            key={index}
          >
            <QuestionIcon type={type} hideText />
          </span>
        ))}
        <ConditionalRenderer condition={editing}>
          <Tooltip
            text={editingAllowed ? t('remove') : editRulesT('cantModify')}
            className="h-fit"
            classNameContainer="flex items-center"
          >
            <IconButton
              loadingSize="w-4"
              className="text-primary flex-row-reverse gap-1 font-500 "
              color="custom"
              onClick={() => setRemoving(prev => !prev)}
              icon={<TrashIcon className="w-4 h-4 text-error" />}
              disabled={loadingRemove || !editingAllowed}
            />
          </Tooltip>
        </ConditionalRenderer>
      </li>
    );
  }

  return null;
}
